import { useBiometrics } from '@/components/AppLock'
import { FeedbackSheet } from '@/components/FeedbackSheet'
import { AddressForm } from '@/components/ProfilePage/AddressForm'
import { ProfileInfoForm } from '@/components/ProfilePage/ProfileInfoForm'
import { ProfileRow } from '@/components/ProfilePage/ProfileRow'
import { RowGroup } from '@/components/Rows'
import { TabScreenHeader } from '@/components/TabScreenHeader'
import { colors } from '@/constants/colors'
import { analytics } from '@/lib/analytics'
import { useAuthStore, useProfileStore } from '@/store'
import { Button, ButtonText } from '@/ui/core/button'
import { Header } from '@/ui/core/header'
import { PageShell } from '@/ui/core/page-shell'
import { Screen } from '@/ui/core/screen'
import { Sheet } from '@/ui/core/sheet'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useQueryClient } from '@tanstack/react-query'
import * as Application from 'expo-application'
import * as Linking from 'expo-linking'
import * as LocalAuthentication from 'expo-local-authentication'
import { router, useLocalSearchParams } from 'expo-router'
import { useEffect, useState } from 'react'
import { Switch, View } from 'react-native'
import Images from '#/assets/images/image-map'
import { firebaseAuth } from '#/firebase'

const PRIVACY_URL = 'https://www.carebywally.com/privacy-policy'

/**
 * The member's own tab: a greeting, then groups of plain rows, each form in a sheet. Sign out and the version are last and quiet.
 */
export default function ProfileScreen() {
	const { faceIdLock, setFaceIdLock } = useProfileStore()
	const biometrics = useBiometrics()
	const { signOut } = useAuthStore()
	const queryClient = useQueryClient()
	const [isSigningOut, setIsSigningOut] = useState(false)
	// Home's whitening sheet sends people straight to the address form, whether or not this tab
	// has been opened before.
	const params = useLocalSearchParams<{ sheet?: string }>()
	const [sheet, setSheet] = useState<'details' | 'address' | 'feedback' | null>(null)
	useEffect(() => {
		if (params.sheet === 'address' || params.sheet === 'details') {
			setSheet(params.sheet)
			router.setParams({ sheet: undefined })
		}
	}, [params.sheet])

	// Turning the lock on proves Face ID works here first, so nobody locks themselves out.
	const onToggleLock = async (value: boolean) => {
		if (!value) {
			setFaceIdLock(false)
			return
		}
		const result = await LocalAuthentication.authenticateAsync({
			promptMessage: 'Turn on Face ID for Wally',
		})
		if (result.success) setFaceIdLock(true)
	}

	const onSignOut = async () => {
		setIsSigningOut(true)
		analytics.trackClick('sign-out')
		try {
			await firebaseAuth.signOut()
		} finally {
			signOut()
			queryClient.clear()
			router.replace('/login')
		}
	}

	return (
		<Screen preset="fixed" className="flex flex-1" safeAreaEdges={[]}>
			<PageShell photo={Images.BrandPhoto} contentClassName="pt-2">
				<TabScreenHeader />
				<VStack gap={12}>
					<View className="pt-6 pb-4">
						<Header variant="h2">Hey, floss boss</Header>
					</View>
					<RowGroup>
						{/* A crown, not a person: the Account tab in the bar directly above this list
						    already wears the person, and a tab and its first row should not say the
						    same thing twice. It suits the greeting over it, too. */}
						<ProfileRow
							glyph="crown"
							title="Personal Details"
							onPress={() => setSheet('details')}
						/>
						<ProfileRow
							glyph="parcel"
							title="Shipping Address"
							onPress={() => setSheet('address')}
						/>
						<ProfileRow
							glyph="switch"
							title="Notifications"
							onPress={() => Linking.openSettings()}
							last
						/>
					</RowGroup>

					{biometrics && (
						<RowGroup>
							<ProfileRow
								glyph="lock"
								title="Face ID App Lock"
								onPress={() => onToggleLock(!faceIdLock)}
								trailing={
									<Switch
										value={faceIdLock}
										onValueChange={onToggleLock}
										trackColor={{ true: colors.accent, false: colors.line }}
										thumbColor={colors.white}
										accessibilityLabel="Face ID app lock"
										accessible={false}
										style={{
											transform: [{ scale: 0.85 }],
											marginRight: 12,
											marginTop: 2,
										}}
									/>
								}
								last
							/>
						</RowGroup>
					)}

					<RowGroup>
						<ProfileRow
							glyph="help"
							title="Help and Support"
							onPress={() => router.navigate('/chat')}
						/>
						{/* Above Privacy rather than below it: this one is a thing to do, and the two
						    under it are reference. */}
						<ProfileRow
							glyph="star"
							title="Leave Feedback"
							onPress={() => setSheet('feedback')}
						/>
						<ProfileRow
							glyph="shield"
							title="Privacy and Data"
							onPress={() => Linking.openURL(PRIVACY_URL)}
							last
						/>
					</RowGroup>

					<VStack gap={12} className="items-center pt-2">
						<Button
							variant="secondary"
							fullWidth={false}
							isLoading={isSigningOut}
							onPress={onSignOut}
							className="self-center"
						>
							<ButtonText>Sign Out</ButtonText>
						</Button>
						<Text size="body2" tone="soft">
							Wally {Application.nativeApplicationVersion ?? ''}
							{Application.nativeBuildVersion
								? ` (${Application.nativeBuildVersion})`
								: ''}
						</Text>
					</VStack>
				</VStack>
			</PageShell>
			<Sheet open={sheet === 'details'} onClose={() => setSheet(null)}>
				<ProfileInfoForm onSaved={() => setSheet(null)} />
			</Sheet>
			<Sheet open={sheet === 'address'} onClose={() => setSheet(null)}>
				<AddressForm onDone={() => setSheet(null)} />
			</Sheet>
			{/* Its own sheet rather than one of the two above, because it holds the whole
			    interaction and closes itself once the answer is away. */}
			<FeedbackSheet open={sheet === 'feedback'} onClose={() => setSheet(null)} />
		</Screen>
	)
}
